import { z } from 'zod';
import { LANGS } from '../constants';
import { MAIL_TEMPLATE_KEYS, MANUAL_TEMPLATE_KEYS } from '../mail';
import { idSchema, updatedAtSchema } from './common';

export const mailTemplateKeySchema = z.enum(MAIL_TEMPLATE_KEYS);

/** The templates a send dialog or Prejemniki may send; the dnevno sporočilo has its own card. */
export const manualTemplateKeySchema = z.enum(MANUAL_TEMPLATE_KEYS);

export const dayMailAutoSchema = z.object({ on: z.boolean() });
export const dayMailSendSchema = z.object({ eventDayId: idSchema });

/**
 * One language of a template. The body is editor HTML — long, but bounded, so
 * a runaway paste cannot fill the column.
 */
export const mailTemplateBodySchema = z.object({
  subject: z.string().trim().min(1).max(300),
  body: z.string().max(40_000),
});

export const mailTemplateUpdateSchema = z.object({
  sl: mailTemplateBodySchema,
  en: mailTemplateBodySchema,
  updatedAt: updatedAtSchema,
});

export type MailTemplateUpdate = z.infer<typeof mailTemplateUpdateSchema>;

/**
 * Preview renders whatever is in the editor, not what is stored, so a
 * coordinator sees the mail before saving. Without a volunteer it falls back to
 * each placeholder's sample value.
 */
export const mailPreviewSchema = z.object({
  lang: z.enum(LANGS).default('sl'),
  subject: z.string().max(300),
  body: z.string().max(40_000),
  volunteerId: idSchema.nullable().default(null),
  /**
   * With no volunteer picked: render the sample as a mail to a shared mailbox
   * — two people — so a coordinator can read their wording for a couple. A
   * real volunteer's preview finds their household on its own.
   */
  household: z.boolean().default(false),
  /**
   * For a volunteer on a shared mailbox: exactly which household members the
   * mail is rendered with — the review dialog's ticked set. Absent, the whole
   * household the template does not refuse.
   */
  with: z.array(idSchema).max(20).optional(),
});

/**
 * One send from the review dialog: an optional one-off edit — the stored
 * template is untouched — and the household members to include in the same
 * mail (skupni predal). Nobody in `with` sends to the addressed person alone.
 */
export const mailSendSchema = z.object({
  override: mailTemplateBodySchema.optional(),
  with: z.array(idSchema).max(20).default([]),
});

export type MailSendInput = z.infer<typeof mailSendSchema>;

export const mailBulkSendSchema = z.object({
  volunteerIds: z.array(idSchema).min(1).max(300),
});

/**
 * One language of an obvestilo. Unlike a template it may be empty: a notice
 * nobody reads in English needs no English, and `noticeProblems` — not the
 * schema — decides whether this send needs it.
 */
export const mailNoticeBodySchema = z.object({
  subject: z.string().trim().max(300),
  body: z.string().max(40_000),
});

/** An obvestilo written for one send: both languages, and exactly who it goes to. */
export const mailNoticeSendSchema = z.object({
  sl: mailNoticeBodySchema,
  en: mailNoticeBodySchema,
  volunteerIds: z.array(idSchema).min(1).max(300),
  /** Mail somebody who is not coming too — ticked in on purpose (SPEC §1.1K). */
  includeCancelled: z.boolean().default(false),
});

export type MailNoticeSendInput = z.infer<typeof mailNoticeSendSchema>;

/** The obvestilo's draft rendered as a preview — for a picked person, as the send would treat them. */
export const mailNoticePreviewSchema = mailPreviewSchema.extend({
  includeCancelled: z.boolean().default(false),
});

export const mailTranslateSchema = z.object({
  subject: z.string().max(300),
  body: z.string().max(40_000),
});
